/**
 * Downscale an image before uploading it as a background (SC-158).
 *
 * A near-identical `resizeImage` exists in `ProfileEditDialog` with **zero
 * callers** — the avatar path uploads the raw file after a size check, so that
 * code has never run in production. This is a fresh implementation with tests,
 * not an extraction of something proven.
 *
 * Two things it does that the dead version does not:
 *
 *  - **Decodes at the target size where possible.** A 15 MB JPEG can be ~48 MP,
 *    which is roughly 192 MB of RGBA once painted to a canvas — a plausible
 *    out-of-memory crash on exactly the weak devices this feature already treats
 *    as the risk case. `createImageBitmap` with resize options lets the browser
 *    scale during decode instead of materialising the full bitmap. Safari's
 *    support for those options has historically lagged, so there is a canvas
 *    fallback.
 *  - **Rejects absurd dimensions up front** rather than discovering them by
 *    running out of memory.
 */

/** Backgrounds are composited into a video frame; beyond 1080p is wasted bytes. */
export const BACKGROUND_MAX_WIDTH = 1920
export const BACKGROUND_MAX_HEIGHT = 1080

/**
 * Refuse anything beyond this on a side. Well above any real photo, and far
 * below the point where decoding would take the tab down.
 */
export const BACKGROUND_MAX_SOURCE_DIMENSION = 12000

/** JPEG at 0.85 puts a 1920x1080 plate around 200-500 KB. */
const OUTPUT_TYPE = 'image/jpeg'
const OUTPUT_QUALITY = 0.85

export interface DownscaleResult {
  blob: Blob
  width: number
  height: number
}

export class ImageTooLargeError extends Error {
  constructor(width: number, height: number) {
    super(
      `That image is ${width}x${height}, which is too large to process. ` +
        `Please use one under ${BACKGROUND_MAX_SOURCE_DIMENSION} pixels on a side.`
    )
    this.name = 'ImageTooLargeError'
  }
}

/** Longest-side fit, never upscaling — enlarging a small image only adds bytes. */
export function fitWithin(
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  if (width <= 0 || height <= 0) return { width: 0, height: 0 }
  const scale = Math.min(maxWidth / width, maxHeight / height, 1)
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

async function decodeToBitmap(file: Blob, target: { width: number; height: number }) {
  // Preferred: the browser scales while decoding, so the full-resolution bitmap
  // is never materialised.
  try {
    return await createImageBitmap(file, {
      resizeWidth: target.width,
      resizeHeight: target.height,
      resizeQuality: 'high',
    })
  } catch {
    // Safari and older browsers ignore or reject the resize options.
    return await createImageBitmap(file)
  }
}

/**
 * Read an image's intrinsic size **without** materialising a full bitmap.
 *
 * This used to call `createImageBitmap(file)`, which defeated the very guard it
 * feeds: rejecting a 48 MP image for being too large to decode required decoding
 * it into ~192 MB of RGBA first. An `<img>` element exposes `naturalWidth` and
 * `naturalHeight` while leaving the decoded data for the browser to manage and
 * discard.
 */
async function measure(file: Blob): Promise<{ width: number; height: number }> {
  const url = URL.createObjectURL(file)
  try {
    const size = await new Promise<{ width: number; height: number }>((resolve, reject) => {
      const image = new Image()
      image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight })
      image.onerror = () => reject(new Error('That file could not be read as an image.'))
      image.src = url
    })
    if (!size.width || !size.height) {
      throw new Error('That file could not be read as an image.')
    }
    return size
  } finally {
    URL.revokeObjectURL(url)
  }
}

/**
 * Downscale `file` to fit within the background bounds and re-encode as JPEG.
 *
 * Throws {@link ImageTooLargeError} for a source big enough to be a memory risk,
 * and a plain Error when the browser cannot decode the file at all — both are
 * worth telling the user about, unlike the silent failure the dead avatar
 * version had (its `toBlob` callback simply never resolved on failure).
 */
export async function downscaleBackgroundImage(
  file: Blob,
  options: { maxWidth?: number; maxHeight?: number } = {}
): Promise<DownscaleResult> {
  const maxWidth = options.maxWidth ?? BACKGROUND_MAX_WIDTH
  const maxHeight = options.maxHeight ?? BACKGROUND_MAX_HEIGHT

  const source = await measure(file)
  if (
    source.width > BACKGROUND_MAX_SOURCE_DIMENSION ||
    source.height > BACKGROUND_MAX_SOURCE_DIMENSION
  ) {
    throw new ImageTooLargeError(source.width, source.height)
  }

  const target = fitWithin(source.width, source.height, maxWidth, maxHeight)
  const bitmap = await decodeToBitmap(file, target)

  try {
    const canvas = document.createElement('canvas')
    canvas.width = target.width
    canvas.height = target.height
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Could not prepare the image for upload')

    context.drawImage(bitmap, 0, 0, target.width, target.height)

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, OUTPUT_TYPE, OUTPUT_QUALITY)
    })
    // toBlob hands back null on failure. The dead avatar version ignored that
    // case and left its promise pending for ever.
    if (!blob) throw new Error('Could not prepare the image for upload')

    return { blob, width: target.width, height: target.height }
  } finally {
    bitmap.close?.()
  }
}

export default downscaleBackgroundImage
